import { Button, Form, Select, Space, TableProps, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useGetUsersWithDisbursementAccessQuery,
  useGetUserWalletRoleByIdQuery,
} from "../../../../features/portal/companyOnboard/companyOnboardApi";
import {
  useCreateUserPoolMutation,
  useGetUserPoolByWalletIdQuery,
} from "../../../../features/portal/Products/productsApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";

import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import useModal from "../../../../hooks/useModal";
import {
  GetUserPoolByWalletIdDataType,
  GetUserPoolByWalletIdParamsType,
} from "../../../../types/portal/product";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import UserPollDeleteModal from "../../Users/UsersList/UserPollDeleteModal";

export default function UserPoolAssignList() {
  const backLink = "/admin/sheba-pay-wallet";
  const [form] = Form.useForm();
  const { id } = useParams();
  const [formType, setFormType] = useState<"pool" | null>(null);
  const { open, handleOpen, handleClose } = useModal<string, any>();

  const { page, limit, handlePagination, handleLimit } =
    useTable<GetUserPoolByWalletIdParamsType>();

  const { user } = useAppSelector((state) => state.auth);

  const { data, isError, error, isLoading } = useGetUserPoolByWalletIdQuery(
    {
      params: {
        wallet_id: Number(id),
        page,
        limit,
      },
    },
    {
      skip: !id,
    }
  );

  const {
    data: getUserWalletRoleByIdData,
    isError: getUserWalletRoleByIdIsError,
    error: getUserWalletRoleByIdError,
    isLoading: getUserWalletRoleByIdIsLoading,
  } = useGetUserWalletRoleByIdQuery(
    {
      id: Number(id),
      role: "approver",
      page: 1,
      limit: 1000000,
    },
    {
      skip: !id,
    }
  );

  const [
    createUserPool,
    {
      isError: isCreatePoolError,
      error: createPollError,
      isLoading: isCreatePoolLoading,
      isSuccess: isCreatePoolSuccess,
    },
  ] = useCreateUserPoolMutation();

  const {
    data: getUsersWithDisbursementAccessData,
    isError: getUsersWithDisbursementAccessIsError,
    error: getUsersWithDisbursementAccessError,
  } = useGetUsersWithDisbursementAccessQuery(
    {
      params: {
        companyId: user?.data?.company_id,
      },
    },
    { skip: !user?.data?.company_id }
  );

  useError(isError, error, "Get user pool by wallet id fail");

  useError(
    getUserWalletRoleByIdIsError,
    getUserWalletRoleByIdError,
    "Get user wallet role by id fail"
  );
  useError(isCreatePoolError, createPollError, "User Pool Assign fail");
  useError(
    getUsersWithDisbursementAccessIsError,
    getUsersWithDisbursementAccessError,
    "Get users with disbursement access fail"
  );

  const onFinishPoolAssign = (values: any) => {
    const filteredUserData =
      getUsersWithDisbursementAccessData?.data?.users?.filter(
        (user: any) => user?.userId === values?.userId
      );

    const newData = {
      userId: filteredUserData && filteredUserData[0]?.userId,
      companyId: filteredUserData && filteredUserData[0]?.user?.company_id,
      poolName: values?.poolName,
      projectId: filteredUserData && filteredUserData[0]?.projectId,
      roleId: filteredUserData && filteredUserData[0]?.roleId,
    };

    // console.log("Success:", newData);
    createUserPool(newData);
  };

  useEffect(() => {
    if (isCreatePoolSuccess) {
      form.resetFields();
      setFormType(null);
      toast("success", "Pool assign successfully");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatePoolSuccess]);

  const columns: TableProps<GetUserPoolByWalletIdDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: "User Name",
      dataIndex: "user",
      render: (val) => (val ? val?.username : "N/A"),
    },

    {
      title: "Role",
      dataIndex: "role",
      align: "center",
      render: (val) => {
        return val?.roleName ? capitalize(val?.roleName) : "N/A";
      },
    },

    {
      title: "Pool Type",
      dataIndex: "poolName",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },

    {
      title: "Actions",
      align: "center",
      render: (record) => (
        <div className="flex justify-center items-center gap-2">
          <Tooltip title="Pool Remove">
            <Button
              onClick={() =>
                handleOpen({
                  type: "UPDATE",
                  data: record,
                })
              }
              className="action-button"
              size="small"
              icon={<DeleteIcon />}
            />
          </Tooltip>
        </div>
      ),
    },
  ];
  return (
    <>
      <SidebarLayout title="Add User Pool" backLink={backLink}>
        {formType === "pool" && (
          <div className="mb-6">
            <Form
              name="form_item_path"
              className="custom-form bg-white border border-extra-light border-solid rounded-md"
              layout="vertical"
              onFinish={onFinishPoolAssign}
              requiredMark="optional"
              size="large"
              form={form}
            >
              <div className="p-6">
                <div className="w-full mt-4 px-3 py-1.5 bg-[#f0f3f8] rounded-md justify-between items-center flex">
                  <div className="justify-start items-center gap-4 flex">
                    <div className="text-display-body text-lg font-semibold leading-[30px]">
                      Assign User Role
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-6 mt-4">
                  <Form.Item
                    label="Select User"
                    name="userId"
                    rules={[{ required: true, message: "Please select user" }]}
                  >
                    <Select
                      placeholder="Select User"
                      className="w-full"
                      suffixIcon={<SelectDownIcon />}
                      style={{ width: "100%" }}
                      allowClear
                      options={
                        getUserWalletRoleByIdData?.data?.userRoles?.map(
                          (item: any) => ({
                            label: item.user.username, //  show username
                            value: item.user.id, //  use user ID as value
                          })
                        ) || []
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Select Pool"
                    name="poolName"
                    rules={[{ required: true, message: "Please select pool" }]}
                  >
                    <Select
                      placeholder="Select Pool"
                      options={[
                        { label: "Pool A", value: "Pool A" },
                        { label: "Pool B", value: "Pool B" },
                      ]}
                      className="w-full"
                      suffixIcon={<SelectDownIcon />}
                      style={{ width: "100%" }}
                      allowClear
                    />
                  </Form.Item>

                  {/* <div className="mt-6">
                    <Button
                      className="modal-button max-w-[160px] pt-6 pb-6"
                      type="primary"
                      htmlType="submit"
                      size="large"
                      // loading={isAssignRoleLoading}
                      // disabled={isSubmitDisabledRole}
                    >
                      Submit
                    </Button>
                  </div> */}
                </div>

                <Space className="w-full mt-2" size={12}>
                  <Button
                    className="normal-dark-button modal-button !h-[40px]"
                    size="middle"
                    onClick={() => setFormType(null)}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="modal-button !h-[40px]"
                    type="primary"
                    htmlType="submit"
                    size="middle"
                    loading={isCreatePoolLoading}
                  >
                    Submit
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        )}

        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.poolData || [])}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => setFormType("pool")}
              >
                Pool Assign
              </Button>
            </>
          }
        />
      </SidebarLayout>

      {open?.type === "UPDATE" && (
        <UserPollDeleteModal
          title=""
          subTitle=""
          buttonName="Send"
          open={open?.open}
          data={open?.data}
          handleClose={handleClose}
          action
          buttonBgDeep
          loading={isLoading}
        />
      )}
    </>
  );
}
